/* Forms 2615 adapter, Contact enquiry 1174937 only.
 * Native submit marks an attempt; only the observed 202 JSON contract accepts it.
 * Default observe. Sending additionally requires an explicit release flag and MAIN.
 * No fields/tokens are retained, logged or sent to Ads. No server ID is invented.
 * Attempt IDs live only in this document. gtag return means queued, not received.
 */
(() => {
  'use strict';
  if (window.location.pathname !== '/pages/contact') return;
  if (window.__zoomtecFormsEnquiryBridge || typeof window.fetch !== 'function') return;
  const config = document.currentScript?.dataset || {};
  const mode = config.zoomtecMode || 'observe';
  const releaseEnabled = config.zoomtecAdsEnabled === 'true';
  const state = { version: 3, mode, adsEnabled: false, acceptedResponses: 0,
    acceptedAttempts: 0, eligibleResponses: 0, boundFormRoots: 0, queuedConversions: 0,
    uncertainCalls: 0, googleReceipt: 'unverified', status: 'ready' };
  window.__zoomtecFormsEnquiryBridge = state;
  const mark = status => {
    state.status = status;
    document.documentElement.dataset.zoomtecFormsEnquiryStatus = status;
  };
  const isolationReason = () => {
    const shopify = window.Shopify;
    const url = new URL(window.location.href);
    if (shopify?.designMode === true || window.top !== window ||
        [...url.searchParams.keys()].some(key => /^(preview|_preview)/i.test(key))) return 'preview-excluded';
    if (shopify?.theme?.role !== 'main') return 'non-main-or-unknown-theme-excluded';
    if (shopify.shop !== 'suaffr-h5.myshopify.com') return 'unknown-shop-excluded';
    if (['zoomtec_qa', 'qa', 'test'].some(key => url.searchParams.has(key))) return 'qa-page-excluded';
    return null;
  };
  const currentMarketingAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.marketingAllowed === 'function' && privacy.marketingAllowed() === true;
    } catch (_) { return false; }
  };
  // Read strings transiently to derive only a boolean; never retain field values.
  const containsTest = value => {
    if (typeof value === 'string') return /\bTEST\b|ZOOMTEC[\s_-]*QA|测试|測試/i.test(value);
    if (value && typeof value === 'object') return Object.entries(value).some(([key, item]) =>
      key !== 'h_captcha_response' && containsTest(item));
    return false;
  };
  let activeAttempt = null;
  const boundRoots = new WeakSet();
  const bindRoot = root => {
    if (!root || boundRoots.has(root)) return;
    boundRoots.add(root);
    state.boundFormRoots += 1;
    root.addEventListener('submit', event => {
      if (event.isTrusted !== true || !event.target?.matches('form[data-testid="form"]')) return;
      // The actual client disables submit while loading. Repeated in-flight
      // native events share its attempt; automatic fetch retries emit no submit.
      if (activeAttempt?.pending > 0 && !activeAttempt.accepted) return;
      if (typeof window.crypto?.randomUUID !== 'function') { activeAttempt = null; mark('attempt-id-unavailable'); return; }
      activeAttempt = { id: window.crypto.randomUUID(), started: Date.now(), pending: 0,
        accepted: false, isTest: false, marketingAllowed: true, sendState: 'idle' };
      mark('attempt-started');
    }, true);
  };
  const bindForm = () => {
    // Inline renderer Fde uses #app-embed inside its form-specific container;
    // floating renderer AB uses #app-embed-container-ID (Forms 2615 source).
    for (const host of document.querySelectorAll('[data-forms-id="forms-root-1174937"] shopify-forms-embed')) {
      bindRoot(host.shadowRoot);
    }
    bindRoot(document.getElementById('app-embed-container-1174937')?.shadowRoot);
  };
  bindForm();
  if (typeof window.MutationObserver === 'function') {
    new window.MutationObserver(bindForm).observe(document.documentElement, { childList: true, subtree: true });
  }
  window.customElements?.whenDefined('shopify-forms-embed').then(bindForm).catch(() => {});
  const originalFetch = window.fetch;
  const observedResponses = new WeakSet();
  const candidate = (input, init) => {
    try {
      const url = new URL(typeof input === 'string' ? input : input.url, window.location.href);
      if (url.origin !== 'https://forms.shopifyapps.com' || url.pathname !== '/api/v2/form_submission') return null;
      if ((init?.method || input?.method || 'GET').toUpperCase() !== 'POST' || typeof init?.body !== 'string') return null;
      const body = JSON.parse(init.body);
      if (String(body.form_instance_id) !== '1174937' || body.shopify_domain !== 'suaffr-h5.myshopify.com') return null;
      return { marketingAllowed: body.privacy_marketing_allowed === true, isTest: containsTest(body) };
    } catch (_) { return null; }
  };
  const send = (attempt, deadline) => {
    if (attempt.sendState === 'queued' || attempt.sendState === 'uncertain' || attempt.sendState === 'excluded') return;
    const reason = isolationReason();
    state.adsEnabled = mode === 'send' && releaseEnabled && !reason;
    if (attempt.isTest || reason || mode !== 'send' || !releaseEnabled ||
        !attempt.marketingAllowed || !currentMarketingAllowed()) {
      attempt.sendState = 'excluded';
      mark(attempt.isTest ? 'test-excluded' : reason || (mode !== 'send' ? 'accepted-response-observed-only' :
        !releaseEnabled ? 'release-disabled' : 'consent-denied'));
      return;
    }
    if (typeof window.gtag !== 'function') {
      if (Date.now() >= deadline) { attempt.sendState = 'excluded'; mark('tag-unavailable-expired'); return; }
      if (attempt.sendState !== 'waiting-tag') {
        attempt.sendState = 'waiting-tag';
        window.setTimeout(() => {
          if (attempt.sendState !== 'waiting-tag') return;
          attempt.sendState = 'idle'; send(attempt, deadline);
        }, 250);
      }
      mark('waiting-for-tag');
      return;
    }
    // Mark before calling an external tag. A throw could happen after enqueue;
    // do not retry an ambiguous call and claim delivery. Same-attempt duplicates
    // always use the same random transaction_id, never customer data or a token.
    attempt.sendState = 'uncertain';
    try {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18353060953/bC_xCKng2tgcENn4ta9E', transaction_id: attempt.id
      });
      attempt.sendState = 'queued';
      state.queuedConversions += 1;
      mark('conversion-queued-receipt-unverified');
    } catch (_) { state.uncertainCalls += 1; mark('tag-call-error-receipt-unknown'); }
  };
  const inspect = async (response, details, attempt) => {
    if (observedResponses.has(response) || response.status !== 202 || response.redirected ||
        !/\bapplication\/json\b/i.test(response.headers.get('content-type') || '')) return;
    try {
      const result = await response.clone().json();
      if (result?.message !== 'Subscribed Successfully.' || !result.errors ||
          typeof result.errors !== 'object' || Array.isArray(result.errors) || Object.keys(result.errors).length) return;
      if (observedResponses.has(response)) return;
      observedResponses.add(response);
      state.acceptedResponses += 1;
      if (attempt && !attempt.accepted) { attempt.accepted = true; state.acceptedAttempts += 1; }
      if (details.marketingAllowed && currentMarketingAllowed()) state.eligibleResponses += 1;
      if (!attempt) { mark(details.isTest ? 'test-excluded' : 'accepted-without-trusted-attempt'); return; }
      send(attempt, Date.now() + 5000);
    } catch (_) { /* Inspection failures never change the original form result. */ }
  };
  window.fetch = function(input, init) {
    const details = candidate(input, init);
    const attempt = details && activeAttempt && Date.now() - activeAttempt.started <= 900000 ? activeAttempt : null;
    if (attempt) {
      attempt.isTest = attempt.isTest || details.isTest;
      attempt.marketingAllowed = attempt.marketingAllowed && details.marketingAllowed;
      attempt.pending += 1;
    }
    let request;
    try { request = originalFetch.apply(this, arguments); }
    catch (error) { if (attempt) attempt.pending -= 1; throw error; }
    if (details) Promise.resolve(request).then(response => {
      if (attempt) attempt.pending -= 1;
      return inspect(response, details, attempt);
    }, () => { if (attempt) attempt.pending -= 1; }).catch(() => {});
    return request;
  };
  mark('ready');
})();
